<template>
  <div class="code-editor-container">
    <div class="code-editor-header">
      <span class="language-label">{{ languageLabel }}</span>
             <div class="editor-controls">
         <el-button size="small" @click="formatCode" :disabled="!modelValue">格式化</el-button>
         <el-button size="small" @click="clearCode" :disabled="!modelValue">清空</el-button>
         <el-button size="small" @click="showTestDialog" :disabled="!modelValue">测试</el-button>
         <el-button size="small" @click="toggleFullscreen" :disabled="!modelValue">
           {{ isFullscreen ? '退出全屏' : '全屏' }}
         </el-button>
         <el-button size="small" @click="toggleTheme">
           {{ currentTheme === 'light' ? '深色' : '浅色' }}
         </el-button>
       </div>
    </div>
         <div
       ref="editorContainer"
       class="codemirror-container"
       :style="{ height: height }"
       :class="{ 'fullscreen': isFullscreen }"
     ></div>
   </div>

       <!-- 测试对话框 -->
    <el-dialog
      v-model:model-value="testDialogVisible"
      title="代码测试"
      width="80%"
      :close-on-click-modal="false"
    >
           <div class="test-dialog-content">
                 <div class="params-section">
           <h4>参数设置</h4>
           <div v-if="testParamsDescription" class="params-description-container">
             <div class="description-header" @click="toggleDescription">
               <span class="description-title">参数说明</span>
               <el-icon class="description-icon" :class="{ 'is-expanded': descriptionExpanded }">
                 <ArrowDown />
               </el-icon>
             </div>
             <div class="params-description" :class="{ 'expanded': descriptionExpanded }">
               {{ testParamsDescription }}
             </div>
           </div>
                               <div class="params-input">
             <div class="params-grid">
               <div class="param-item" v-for="(param, index) in testParams" :key="index">
                 <div class="param-header">
                                       <el-input
                      v-model="param.key"
                      placeholder="参数名"
                      size="small"
                      style="width: 100px;"
                    />
                   <el-button
                     type="danger"
                     size="small"
                     @click="removeParam(index)"
                     :disabled="testParams.length <= 1"
                   >
                     删除
                   </el-button>
                 </div>
                 <div class="param-value-container">
                   <!-- 对象或数组类型使用JSON编辑器 -->
                   <div v-if="isObjectValue(param.value)" class="json-editor-wrapper">
                     <JsonEditorVue 
                       language="zh-CN" 
                       currentMode="tree"  
                       style="width: 100%; height: 300px" 
                       v-model="param.jsonValue" 
                       @change="onJsonChange(index, $event)"
                     />
                   </div>
                   <!-- 非对象类型使用普通输入框 -->
                   <el-input
                     v-else
                     v-model="param.value"
                     placeholder="参数值"
                     size="small"
                     style="width: 100%;"
                   />
                 </div>
               </div>
             </div>
             <div class="add-param-section">
               <el-button type="primary" size="small" @click="addParam">添加参数</el-button>
             </div>
           </div>
       </div>

       <div class="result-section" v-if="testResult">
         <h4>执行结果</h4>
         <div class="result-content">
           <div class="result-item">
             <span class="result-label">状态:</span>
             <el-tag :type="testResult.success ? 'success' : 'danger'">
               {{ testResult.success ? '成功' : '失败' }}
             </el-tag>
           </div>
           <div class="result-item" v-if="testResult.data">
             <span class="result-label">返回值:</span>
             <div class="result-value">{{ testResult.data }}</div>
           </div>
           <div class="result-item" v-if="testResult.error">
             <span class="result-label">错误信息:</span>
             <div class="result-error">{{ testResult.error }}</div>
           </div>
         </div>
       </div>
     </div>

     <template #footer>
       <div class="dialog-footer">
         <el-button @click="testDialogVisible = false">取消</el-button>
         <el-button type="primary" @click="executeTest" :loading="testLoading">
           执行测试
         </el-button>
       </div>
     </template>
   </el-dialog>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { EditorView } from '@codemirror/view'
import { EditorState } from '@codemirror/state'
import { basicSetup } from 'codemirror'
import { javascript } from '@codemirror/lang-javascript'
import { oneDark } from '@codemirror/theme-one-dark'
import { autocompletion } from '@codemirror/autocomplete'
import { lintGutter, lintKeymap } from '@codemirror/lint'
import { search } from '@codemirror/search'
import { keymap } from '@codemirror/view'
import { ElMessage } from 'element-plus'
import { ArrowDown } from '@element-plus/icons-vue'
import JsonEditorVue from 'json-editor-vue3'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  language: {
    type: String,
    default: 'javascript'
  },
  height: {
    type: String,
    default: '300px'
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  showLineNumbers: {
    type: Boolean,
    default: true
  },
  placeholder: {
    type: String,
    default: '请输入JavaScript代码...'
  },
  fontSize: {
    type: Number,
    default: 14
  },
  theme: {
    type: String,
    default: 'light'
  },
  // 新增：测试参数配置
  testParamsConfig: {
    type: Array,
    default: () => [
      { key: 'wcs', value: '{"chousuilvList":[0.6,0.5]}' },
      { key: 'val', value: '0.5' },
      { key: 'count', value: '10' },
      { key: 'sb', value: '{"chousuilv":0.6}' }
    ]
  },
  // 新增：异步加载测试参数函数
  loadTestParams: {
    type: Function,
    default: null
  },
  // 新增：测试参数描述信息
  testParamsDescription: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const editorContainer = ref(null)
const editorView = ref(null)
const isFullscreen = ref(false)
const currentTheme = ref(props.theme)

// 测试相关状态
const testDialogVisible = ref(false)
const testParams = ref([])
const testResult = ref(null)
const testLoading = ref(false)
const descriptionExpanded = ref(false)

// 初始化测试参数
const initTestParams = async () => {
  let params = []
  
  // 如果有异步加载函数，优先使用异步加载
  if (props.loadTestParams && typeof props.loadTestParams === 'function') {
    try {
      const asyncParams = await props.loadTestParams();
      params = asyncParams || [];
    } catch (error) {
      console.error('异步加载测试参数失败:', error);
      // 如果异步加载失败，回退到静态配置
      params = JSON.parse(JSON.stringify(props.testParamsConfig || []));
    }
  } else {
    // 深拷贝传入的测试参数配置，避免修改原始数据
    params = JSON.parse(JSON.stringify(props.testParamsConfig || []));
  }
  
  // 为对象类型的参数添加jsonValue属性
  testParams.value = params.map(param => {
    if (isObjectValue(param.value)) {
      try {
        return {
          ...param,
          jsonValue: JSON.parse(param.value)
        }
      } catch {
        return param
      }
    }
    return param
  })
}

// 语言标签映射
const languageLabel = computed(() => {
  const langMap = {
    'javascript': 'JavaScript',
    'typescript': 'TypeScript',
    'json': 'JSON',
    'html': 'HTML',
    'css': 'CSS',
    'python': 'Python',
    'java': 'Java',
    'cpp': 'C++',
    'csharp': 'C#',
    'php': 'PHP',
    'sql': 'SQL'
  }
  return langMap[props.language] || props.language.toUpperCase()
})

// 创建编辑器配置
const createEditorConfig = (content = '') => {
  const extensions = [
    basicSetup,
    javascript(),
    autocompletion(),
    lintGutter(),
    search(),
    keymap.of([
      ...lintKeymap
    ]),
    EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        const value = update.state.doc.toString()
        emit('update:modelValue', value)
        emit('change', value)
      }
    }),
    EditorView.theme({
      '&': {
        fontSize: `${props.fontSize}px`,
        fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace"
      },
      '.cm-scroller': {
        fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace"
      },
      // 增强选中样式
      '.cm-selectionBackground': {
        background: '#ffd700 !important',
        opacity: '0.6'
      },
      '.cm-selectionMatch': {
        background: '#ffd700 !important',
        opacity: '0.4'
      }
    })
  ]

  // 添加主题
  if (currentTheme.value === 'dark') {
    extensions.push(oneDark)
  }

  // 添加只读模式
  if (props.readOnly) {
    extensions.push(EditorState.readOnly.of(true))
  }

  return {
    doc: content || props.placeholder,
    extensions
  }
}

// 初始化编辑器
const initEditor = () => {
  if (!editorContainer.value) return

  try {
    // 确保使用当前的 modelValue，而不是缓存的内容
    const config = createEditorConfig(props.modelValue || '')
    const state = EditorState.create(config)
    editorView.value = new EditorView({
      state,
      parent: editorContainer.value
    })
  } catch (error) {
    console.error('CodeMirror初始化失败:', error)
    fallbackToTextarea()
  }
}

// 回退到简单textarea
const fallbackToTextarea = () => {
  if (!editorContainer.value) return

  const textarea = document.createElement('textarea')
  textarea.className = 'fallback-textarea'
  textarea.value = props.modelValue || ''
  textarea.placeholder = props.placeholder
  textarea.style.cssText = `
    width: 100%;
    height: 100%;
    border: none;
    outline: none;
    resize: none;
    padding: 12px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: ${props.fontSize}px;
    line-height: 1.5;
    color: #333;
    background: #ffffff;
  `

  textarea.addEventListener('input', (e) => {
    emit('update:modelValue', e.target.value)
    emit('change', e.target.value)
  })

  editorContainer.value.appendChild(textarea)
}

// 格式化代码
const formatCode = () => {
  if (!editorView.value) return

  try {
    // 简单的代码格式化
    const content = editorView.value.state.doc.toString()
    if (!content.trim()) return

    // 基本的缩进格式化
    const lines = content.split('\n')
    const formattedLines = lines.map((line, index) => {
      const trimmed = line.trim()
      if (!trimmed) return ''

      // 简单的缩进逻辑
      let indent = 0
      if (trimmed.includes('{')) indent++
      if (trimmed.includes('}')) indent--

      return '  '.repeat(Math.max(0, indent)) + trimmed
    })

    const formatted = formattedLines.join('\n')
    updateEditorContent(formatted)
  } catch (error) {
    console.error('代码格式化失败:', error)
  }
}

// 清空代码
const clearCode = () => {
  updateEditorContent('')
}

// 切换全屏
const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value
  nextTick(() => {
    if (editorView.value) {
      editorView.value.requestMeasure()
    }
  })
}

// 切换主题
const toggleTheme = () => {
  currentTheme.value = currentTheme.value === 'light' ? 'dark' : 'light'
  if (editorView.value) {
    // 重新创建编辑器以应用新主题
    const content = editorView.value.state.doc.toString()
    editorView.value.destroy()
    editorView.value = null
    nextTick(() => {
      initEditor()
      if (content) {
        updateEditorContent(content)
      }
    })
  }
}

// 切换注释
const toggleComment = () => {
  if (!editorView.value) return false

  const { state, dispatch } = editorView.value
  const { from, to } = state.selection

  const line = state.doc.lineAt(from)
  const lineText = line.text
  const isCommented = lineText.trim().startsWith('//')

  if (isCommented) {
    // 取消注释
    const newText = lineText.replace(/^(\s*)\/\/\s*/, '$1')
    dispatch({
      changes: { from: line.from, to: line.to, insert: newText }
    })
  } else {
    // 添加注释
    const indent = lineText.match(/^\s*/)[0]
    const newText = indent + '// ' + lineText.trim()
    dispatch({
      changes: { from: line.from, to: line.to, insert: newText }
    })
  }

  return true
}

// 显示测试对话框
const showTestDialog = async () => {
  testDialogVisible.value = true
  testResult.value = null
  // 每次打开测试对话框时重新初始化参数
  await initTestParams()
}

// 添加参数
const addParam = () => {
  testParams.value.push({ key: '', value: '' })
}

// 删除参数
const removeParam = (index) => {
  testParams.value.splice(index, 1)
}

// 切换描述信息展开/收起
const toggleDescription = () => {
  descriptionExpanded.value = !descriptionExpanded.value
}

// 判断是否为对象或数组类型的值
const isObjectValue = (value) => {
  if (typeof value !== 'string') return false
  try {
    const parsed = JSON.parse(value)
    return typeof parsed === 'object' && parsed !== null
  } catch {
    return false
  }
}

// JSON编辑器值变化处理
const onJsonChange = (index, newValue) => {
  if (testParams.value[index]) {
    testParams.value[index].jsonValue = newValue
    testParams.value[index].value = JSON.stringify(newValue)
  }
}

// 执行测试
const executeTest = async () => {
  if (!props.modelValue || !props.modelValue.trim()) {
    ElMessage.warning('请先输入要测试的代码')
    return
  }

  // 验证参数
  const validParams = testParams.value.filter(param => param.key && param.value)
  if (validParams.length === 0) {
    ElMessage.warning('请至少添加一个参数')
    return
  }

  testLoading.value = true
  testResult.value = null

  try {
    // 构建参数对象
    const params = {}
    validParams.forEach(param => {
      // 如果参数有jsonValue属性，直接使用
      if (param.jsonValue !== undefined) {
        params[param.key] = param.jsonValue
      }
      // 否则，如果param.value包含大括号 {}，则需要用JSON.parse解析
      else if (param.value.includes('{')) {
        try {
          params[param.key] = JSON.parse(param.value);
        } catch (error) {
          console.error(`解析参数 ${param.key} 的JSON值失败:`, error);
          params[param.key] = param.value;
        }
      } else {
        params[param.key] = param.value
      }
    })

    // 调用测试接口
    const result = await VE_API.system.testJs({script:props.modelValue,params});

    if (result.code === 0) {
      testResult.value = {
        success: true,
        data: result.msg,
        error: null
      }
      ElMessage.success('测试执行成功')
    } else {
      testResult.value = {
        success: false,
        data:  result.msg,
        error:  result.msg,
      }
      ElMessage.error('测试执行失败')
    }
  } catch (error) {
    console.error('测试执行错误:', error)
    testResult.value = {
      success: false,
      data: null,
      error: error.message || '网络错误或服务器异常'
    }
    ElMessage.error('测试执行失败: ' + error.message)
  } finally {
    testLoading.value = false
  }
}

// 更新编辑器内容
const updateEditorContent = (content) => {
  if (!editorView.value) return

  try {
    const docLength = editorView.value.state.doc.length
    editorView.value.dispatch({
      changes: {
        from: 0,
        to: docLength,
        insert: content
      }
    })
  } catch (error) {
    console.error('更新编辑器内容失败:', error)
  }
}

// 监听modelValue变化
watch(() => props.modelValue, (newValue) => {
  if (!editorView.value) return

  const currentContent = editorView.value.state.doc.toString()
  if (newValue !== currentContent) {
    updateEditorContent(newValue || '')
  }
})

// 监听language变化，重新创建编辑器
watch(() => props.language, () => {
  if (editorView.value) {
    const content = editorView.value.state.doc.toString()
    editorView.value.destroy()
    editorView.value = null
    nextTick(() => {
      initEditor()
      // 重置为初始值，而不是保留之前的内容
      updateEditorContent(props.modelValue || '')
    })
  }
})

// 监听字体大小变化
watch(() => props.fontSize, (newSize) => {
  if (editorView.value) {
    // 重新创建编辑器以应用新字体大小
    const content = editorView.value.state.doc.toString()
    editorView.value.destroy()
    editorView.value = null
    nextTick(() => {
      initEditor()
      if (content) {
        updateEditorContent(content)
      }
    })
  }
})

onMounted(() => {
  nextTick(async () => {
    // 确保在挂载时完全重置编辑器状态
    if (editorView.value) {
      editorView.value.destroy()
      editorView.value = null
    }
    initEditor()
    // 初始化测试参数
    await initTestParams()
  })
})

onBeforeUnmount(() => {
  if (editorView.value) {
    editorView.value.destroy()
    editorView.value = null
  }
})

// 暴露方法给父组件
defineExpose({
  getValue: () => {
    if (!editorView.value) return ''
    return editorView.value.state.doc.toString()
  },
  setValue: (value) => {
    updateEditorContent(value || '')
  },
  focus: () => editorView.value?.focus(),
  formatCode,
  clearCode,
  reset: () => {
    // 重置编辑器到初始状态
    if (editorView.value) {
      editorView.value.destroy()
      editorView.value = null
    }
    nextTick(() => {
      initEditor()
    })
  },
  getEditor: () => editorView.value
})
</script>

<style scoped>
.code-editor-container {
  width: 100%;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  background: #fafafa;
  position: relative;
}

.code-editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f5f7fa;
  border-bottom: 1px solid #dcdfe6;
  font-size: 12px;
  z-index: 10;
}

.language-label {
  color: #606266;
  font-weight: 500;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
}

.editor-controls {
  display: flex;
  gap: 8px;
}

.codemirror-container {
  position: relative;
  background: #ffffff;
  transition: all 0.3s ease;
}

.codemirror-container.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  border-radius: 0;
}

.codemirror-container.fullscreen .code-editor-header {
  position: sticky;
  top: 0;
  z-index: 10000;
}

/* 回退textarea样式 */
.fallback-textarea {
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  resize: none;
  padding: 12px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  background: #ffffff;
}

.fallback-textarea::placeholder {
  color: #c0c4cc;
}

/* 焦点状态 */
.code-editor-container:focus-within {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

/* 全屏模式下的样式调整 */
.codemirror-container.fullscreen .code-editor-container {
  border: none;
  border-radius: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .editor-controls {
    flex-wrap: wrap;
    gap: 4px;
  }

  .editor-controls .el-button {
    font-size: 11px;
    padding: 4px 8px;
  }
}

/* CodeMirror 自定义样式 */
:deep(.cm-editor) {
  height: 100%;
}

:deep(.cm-editor .cm-scroller) {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

:deep(.cm-editor .cm-content) {
  padding: 12px;
}

:deep(.cm-editor .cm-line) {
  padding: 0;
}

:deep(.cm-editor .cm-gutters) {
  background: #f8f9fa;
  border-right: 1px solid #e4e7ed;
}

:deep(.cm-editor .cm-activeLineGutter) {
  background: #e6f7ff;
}

:deep(.cm-editor .cm-activeLine) {
  background: #f0f9ff;
}

/* 选中文本样式 - 增强可见性 */
:deep(.cm-editor .cm-selectionBackground) {
  background: #ffd700 !important;
  opacity: 0.6;
}

:deep(.cm-editor .cm-selectionMatch) {
  background: #ffd700 !important;
  opacity: 0.4;
}

/* 变量和标识符高亮 - 更鲜艳的颜色 */
:deep(.cm-editor .cm-variable) {
  color: #e36209;
  font-weight: 600;
}

:deep(.cm-editor .cm-variable-2) {
  color: #6f42c1;
  font-weight: 600;
}

:deep(.cm-editor .cm-variable-3) {
  color: #d73a49;
  font-weight: 600;
}

:deep(.cm-editor .cm-property) {
  color: #005cc5;
  font-weight: 600;
}

:deep(.cm-editor .cm-definition) {
  color: #6f42c1;
  font-weight: 700;
}

/* 全局变量和特殊标识符 */
:deep(.cm-editor .cm-global) {
  color: #ff6b35;
  font-weight: 700;
}

:deep(.cm-editor .cm-builtin) {
  color: #ff6b35;
  font-weight: 700;
}

:deep(.cm-editor .cm-typeName) {
  color: #6f42c1;
  font-weight: 700;
}

:deep(.cm-editor .cm-namespace) {
  color: #ff6b35;
  font-weight: 700;
}

:deep(.cm-editor .cm-modifier) {
  color: #d73a49;
  font-weight: 700;
}

:deep(.cm-editor .cm-attributeName) {
  color: #005cc5;
  font-weight: 600;
}

:deep(.cm-editor .cm-attributeValue) {
  color: #032f62;
  font-weight: 600;
}

/* 关键字高亮 */
:deep(.cm-editor .cm-keyword) {
  color: #d73a49;
  font-weight: 700;
}

:deep(.cm-editor .cm-operator) {
  color: #d73a49;
  font-weight: 600;
}

/* 字符串和注释 */
:deep(.cm-editor .cm-string) {
  color: #032f62;
  font-weight: 600;
}

:deep(.cm-editor .cm-comment) {
  color: #6a737d;
  font-style: italic;
  font-weight: 500;
}

/* 数字 */
:deep(.cm-editor .cm-number) {
  color: #005cc5;
  font-weight: 600;
}

/* 深色主题样式 */
:deep(.cm-editor.cm-focused .cm-selectionBackground) {
  background: #ffd700 !important;
  opacity: 0.7;
}

:deep(.cm-editor.cm-focused .cm-selectionMatch) {
  background: #ffd700 !important;
  opacity: 0.5;
}

/* 光标样式 */
:deep(.cm-editor .cm-cursor) {
  border-left: 2px solid #409eff;
  border-right: none;
  width: 0;
}

/* 搜索高亮 */
:deep(.cm-editor .cm-searchMatch) {
  background: #ffeb3b;
  color: #000;
}

:deep(.cm-editor .cm-searchMatch.cm-searchMatch-selected) {
  background: #ff9800;
  color: #fff;
}

/* 语法错误高亮 */
:deep(.cm-editor .cm-error) {
  background: #ffebee;
  color: #c62828;
  text-decoration: underline;
  text-decoration-color: #c62828;
  text-decoration-style: wavy;
}

/* 悬停效果 */
:deep(.cm-editor .cm-line:hover) {
  background: rgba(64, 158, 255, 0.05);
}

/* 选中行的高亮 */
:deep(.cm-editor .cm-activeLine) {
  background: rgba(64, 158, 255, 0.1) !important;
}

/* 匹配括号高亮 */
:deep(.cm-editor .cm-matchingBracket) {
  background: rgba(64, 158, 255, 0.3);
  border-radius: 2px;
}

:deep(.cm-editor .cm-nonmatchingBracket) {
  background: rgba(244, 67, 54, 0.3);
  border-radius: 2px;
}

/* 增强变量选中时的可见性 */
:deep(.cm-editor .cm-variable.cm-selectionBackground),
:deep(.cm-editor .cm-variable-2.cm-selectionBackground),
:deep(.cm-editor .cm-variable-3.cm-selectionBackground) {
  background: #ffd700 !important;
  color: #000 !important;
  font-weight: 700 !important;
  opacity: 0.9;
}

/* 增强全局变量选中时的可见性 */
:deep(.cm-editor .cm-global.cm-selectionBackground),
:deep(.cm-editor .cm-builtin.cm-selectionBackground),
:deep(.cm-editor .cm-namespace.cm-selectionBackground) {
  background: #ff6b35 !important;
  color: #fff !important;
  font-weight: 700 !important;
  opacity: 0.9;
}

/* 增强属性选中时的可见性 */
:deep(.cm-editor .cm-property.cm-selectionBackground) {
  background: #ffd700 !important;
  color: #000 !important;
  font-weight: 700 !important;
  opacity: 0.9;
}

/* 测试对话框样式 */
.test-dialog-content {
  max-height: 75vh;
  overflow-y: auto;
}

.params-section {
  margin-bottom: 20px;
}

.params-section h4 {
  margin: 0 0 12px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.params-description-container {
  margin-bottom: 12px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background-color: #f8f9fa;
  overflow: hidden;
}

.description-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: #f0f2f5;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.description-header:hover {
  background-color: #e6e8eb;
}

.description-title {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
}

.description-icon {
  font-size: 12px;
  color: #606266;
  transition: transform 0.3s ease;
}

.description-icon.is-expanded {
  transform: rotate(180deg);
}

.params-description {
  padding: 0;
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  white-space: pre-wrap;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  max-height: 0;
  overflow: hidden;
  transition: all 0.3s ease;
  background-color: #f8f9fa;
}

.params-description.expanded {
  padding: 12px;
  max-height: 200px;
  overflow-y: auto;
}

.params-input {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.params-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
}

.param-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background: #fafafa;
}

.param-header {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
}

.param-value-container {
  min-height: 40px;
}

.json-editor-wrapper {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}

.add-param-section {
  display: flex;
  justify-content: center;
  padding-top: 8px;
  border-top: 1px solid #e4e7ed;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .params-grid {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
}

@media (max-width: 768px) {
  .params-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .param-item {
    padding: 8px;
  }
  
  .param-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .param-header .el-input {
    width: 100% !important;
  }
  
  .description-header {
    padding: 6px 10px;
  }
  
  .description-title {
    font-size: 12px;
  }
  
  .params-description.expanded {
    max-height: 150px;
  }
}

.result-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
}

.result-section h4 {
  margin: 0 0 12px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.result-content {
  background: #f8f9fa;
  border-radius: 4px;
  padding: 12px;
}

.result-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
}

.result-item:last-child {
  margin-bottom: 0;
}

.result-label {
  min-width: 80px;
  color: #606266;
  font-weight: 500;
  margin-right: 8px;
}

.result-value {
  flex: 1;
  background: #ffffff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 8px 12px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  color: #303133;
  word-break: break-all;
}

.result-error {
  flex: 1;
  background: #fef0f0;
  border: 1px solid #fbc4c4;
  border-radius: 4px;
  padding: 8px 12px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  color: #f56c6c;
  word-break: break-all;
}

.dialog-footer {
  text-align: right;
}
</style>
